import { useId, useRef, useState, type FormEvent } from 'react';
import { apiErrorText, useEditEconBdrLineMutation, useGetEconBdrLineTraceQuery } from '@shared/api';
import { Button } from '@shared/ui/Button';
import {
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerFooter,
  DrawerHeader,
} from '@shared/ui/Drawer';
import { SkeletonLine } from '@shared/ui/Skeleton';
import {
  Actions,
  Control,
  FieldError,
  FieldHint,
  FieldLabel,
  FleetChip,
  FooterStatus,
  FormGrid,
  FormRow,
  Notice,
  Spacer,
  typo,
} from '@entities/fleet';
import {
  BDR_SOURCE_LABEL,
  formatRub2,
  type BdrArticle,
  type BdrColumn,
  type BdrLine,
} from '@entities/economic/bdrV2';
import { DrawerTitle, Eyebrow, HeaderMeta, HeaderRow, Titles } from '../prc/drawerParts';
import { Chain, DocsTable, Section } from './styles';

type Doc = {
  receipt_id?: string;
  date?: string;
  supplier?: string;
  invoice_no?: string | null;
  item?: string;
  amount?: string;
};

/** Документы прихода ТМЦ в трассировке ячейки материалов. */
function receiptDocs(inputs: unknown): Doc[] {
  if (!inputs || typeof inputs !== 'object') return [];
  const refs = (inputs as { refs?: unknown }).refs;
  return Array.isArray(refs) ? (refs as Doc[]).filter((r) => r && typeof r === 'object') : [];
}

function stepTitle(step: Record<string, unknown>): string {
  const kind = typeof step.kind === 'string' ? step.kind : 'шаг';
  const title = typeof step.title === 'string' ? step.title : null;
  return title ?? kind;
}

/**
 * Строка БДР в боковой панели: откуда сумма (источник, формула, документы),
 * значение источника и ручная правка с причиной. Без права правки — только
 * просмотр; чужая правка между открытием и сохранением — 409 с понятным
 * текстом.
 */
export function LineDrawer({
  line,
  article,
  column,
  canEdit,
  onClose,
  onSaved,
}: {
  line: BdrLine;
  article: BdrArticle | undefined;
  column: BdrColumn;
  canEdit: boolean;
  onClose: () => void;
  onSaved: (text: string) => void;
}) {
  const uid = useId();
  const titleRef = useRef<HTMLHeadingElement>(null);
  const { data: trace, isLoading } = useGetEconBdrLineTraceQuery(line.id);
  const [edit, editState] = useEditEconBdrLineMutation();
  const [value, setValue] = useState(line.manual_amount?.replace('.', ',') ?? '');
  const [reason, setReason] = useState(line.manual_reason ?? '');
  const [error, setError] = useState<string | null>(null);
  const editable = canEdit && article?.editable !== false;

  const chain = Array.isArray((trace as { chain?: unknown } | undefined)?.chain)
    ? ((trace as { chain: Record<string, unknown>[] }).chain ?? [])
    : [];
  const docs = receiptDocs((trace as { inputs?: unknown } | undefined)?.inputs ?? null);

  async function save(e: FormEvent, clear = false) {
    e.preventDefault();
    setError(null);
    if (!clear && value.trim() === '') {
      setError('Впишите сумму или снимите правку');
      return;
    }
    if (!clear && reason.trim() === '') {
      setError('Укажите причину правки — без неё правку не примут');
      return;
    }
    try {
      await edit({
        id: line.id,
        value: clear ? null : value.trim(),
        reason: clear ? null : reason.trim(),
        expected_updated_at: line.updated_at,
      }).unwrap();
      onSaved(clear ? 'Ручная правка снята.' : 'Правка сохранена, итоги пересчитаны.');
    } catch (err) {
      setError(apiErrorText(err, 'Не удалось сохранить правку.'));
    }
  }

  return (
    <Drawer open onClose={onClose} labelledBy={`${uid}-title`} initialFocusRef={titleRef} size="lg">
      <DrawerHeader>
        <HeaderRow>
          <Titles>
            <Eyebrow>
              {column.label} · {BDR_SOURCE_LABEL[line.source] ?? line.source}
            </Eyebrow>
            <DrawerTitle id={`${uid}-title`} ref={titleRef} tabIndex={-1}>
              {article?.template_row ? `${article.template_row}. ` : ''}
              {article?.name ?? line.article_code}
            </DrawerTitle>
            <HeaderMeta>
              {line.no_data && line.manual_amount === null ? (
                <FleetChip $tone="warning">нет данных</FleetChip>
              ) : (
                <FleetChip $tone="success">{formatRub2(line.amount)}</FleetChip>
              )}
              {line.manual_amount !== null && (
                <FleetChip $tone="info">
                  правка {line.manual_by_name ?? ''}: источник {formatRub2(line.source_amount)}
                </FleetChip>
              )}
            </HeaderMeta>
          </Titles>
          <DrawerCloseButton onClick={onClose} />
        </HeaderRow>
      </DrawerHeader>
      <DrawerBody>
        {error && (
          <Notice $tone="danger" role="alert">
            <span className="text">{error}</span>
          </Notice>
        )}
        {line.hint && (
          <Notice $tone="warning" role="note">
            <span className="text">{typo(line.hint)}</span>
          </Notice>
        )}

        <Section aria-labelledby={`${uid}-trace`}>
          <h3 id={`${uid}-trace`}>Откуда сумма</h3>
          {isLoading ? (
            <SkeletonLine $height={60} />
          ) : chain.length === 0 ? (
            <p>{typo('Цепочка источника для этой строки не записана.')}</p>
          ) : (
            <Chain>
              {chain.map((step, i) => (
                <li key={i}>
                  <b>{stepTitle(step)}</b>
                  <pre>{JSON.stringify(step, null, 2)}</pre>
                </li>
              ))}
            </Chain>
          )}
          {docs.length > 0 && (
            <DocsTable>
              <thead>
                <tr>
                  <th>Дата</th>
                  <th>Поставщик</th>
                  <th>Счёт-фактура</th>
                  <th>Материал</th>
                  <th className="num">Без НДС</th>
                </tr>
              </thead>
              <tbody>
                {docs.map((d, i) => (
                  <tr key={d.receipt_id ?? i}>
                    <td>{d.date ?? '—'}</td>
                    <td>{d.supplier ?? '—'}</td>
                    <td>{d.invoice_no ?? '—'}</td>
                    <td>{d.item ?? '—'}</td>
                    <td className="num">{formatRub2(d.amount ?? null)}</td>
                  </tr>
                ))}
              </tbody>
            </DocsTable>
          )}
        </Section>

        {editable && (
          <Section aria-labelledby={`${uid}-edit`}>
            <h3 id={`${uid}-edit`}>Ручная правка</h3>
            <form id={`${uid}-form`} onSubmit={(e) => void save(e)} noValidate>
              <FormGrid>
                <FormRow>
                  <FieldLabel htmlFor={`${uid}-value`}>
                    <span>Сумма, ₽</span>
                  </FieldLabel>
                  <Control
                    id={`${uid}-value`}
                    inputMode="decimal"
                    autoComplete="off"
                    value={value}
                    disabled={editState.isLoading}
                    onChange={(e) => setValue(e.target.value)}
                  />
                  <FieldHint>
                    {typo('Значение источника остаётся рядом — правку видно в журнале.')}
                  </FieldHint>
                </FormRow>
                <FormRow $wide>
                  <FieldLabel htmlFor={`${uid}-reason`}>
                    <span>Причина</span>
                  </FieldLabel>
                  <Control
                    id={`${uid}-reason`}
                    autoComplete="off"
                    value={reason}
                    disabled={editState.isLoading}
                    aria-describedby={error ? `${uid}-err` : undefined}
                    onChange={(e) => setReason(e.target.value)}
                  />
                  {error && <FieldError id={`${uid}-err`}>{error}</FieldError>}
                </FormRow>
              </FormGrid>
            </form>
          </Section>
        )}
      </DrawerBody>
      <DrawerFooter>
        {editable && line.manual_amount !== null && (
          <Button
            type="button"
            $variant="ghost"
            disabled={editState.isLoading}
            onClick={(e) => void save(e as unknown as FormEvent, true)}
          >
            Снять правку
          </Button>
        )}
        <FooterStatus role="status">{editState.isLoading ? 'Сохраняю…' : ''}</FooterStatus>
        <Spacer />
        <Actions>
          <Button type="button" $variant="ghost" onClick={onClose}>
            Закрыть
          </Button>
          {editable && (
            <Button
              type="submit"
              form={`${uid}-form`}
              $variant="primary"
              disabled={editState.isLoading}
            >
              Сохранить правку
            </Button>
          )}
        </Actions>
      </DrawerFooter>
    </Drawer>
  );
}
